import React from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { AIAgent } from "./types";
import {
  Bot,
  MessageSquare,
  DollarSign,
  FileText,
  BarChart3,
  Shield,
  Code,
  Server,
  Wifi,
  Settings,
  Eye,
  EyeOff,
} from "lucide-react";
import { Button } from "../../components/ui/button";

export const ConfigurationTab: React.FC<{ agent: AIAgent }> = ({ agent }) => {
  const [showApiKey, setShowApiKey] = React.useState(false);

  // Ensure agent has the required properties with fallbacks
  const config = agent.config || {
    endpoint: "Not configured",
    apiKey: "",
    timeout: 30000,
    maxTokens: 4000,
    temperature: 0.7,
    modelVersion: "gpt-4",
    features: [],
    serverUrl: "",
    httpHeaders: [],
  };

  const testResults = agent.testResults || {
    totalTests: 0,
    passedTests: 0,
    failedTests: 0,
    lastTestRun: new Date(),
    averageScore: 0,
  };

  // Helper functions for icons and labels
  const getAgentTypeIcon = (type: string) => {
    const iconMap: { [key: string]: React.ReactNode } = {
      conversational: <MessageSquare className="h-4 w-4" />,
      sales: <DollarSign className="h-4 w-4" />,
      content: <FileText className="h-4 w-4" />,
      analytics: <BarChart3 className="h-4 w-4" />,
      safety: <Shield className="h-4 w-4" />,
      development: <Code className="h-4 w-4" />,
    };
    return iconMap[type] || <Bot className="h-4 w-4" />;
  };

  const getAgentTypeLabel = (type: string) => {
    const labelMap: { [key: string]: string } = {
      conversational: "Conversational AI",
      sales: "Sales & Marketing",
      content: "Content Generation",
      analytics: "Data & Analytics",
      safety: "Safety & Compliance",
      development: "Development Assistant",
    };
    return labelMap[type] || type;
  };

  const getIndustryIcon = (industry: string) => {
    const iconMap: { [key: string]: string } = {
      ecommerce: "🛒",
      healthcare: "🏥",
      finance: "🏦",
      education: "🎓",
      technology: "💻",
      media: "🎬",
      consulting: "📋",
      manufacturing: "🏭",
      other: "🔧",
    };
    return iconMap[industry] || "🏢";
  };

  const getIndustryLabel = (industry: string) => {
    const labelMap: { [key: string]: string } = {
      ecommerce: "E-commerce & Retail",
      healthcare: "Healthcare & Medical",
      finance: "Finance & Banking",
      education: "Education & Training",
      technology: "Technology & SaaS",
      media: "Media & Entertainment",
      consulting: "Consulting & Professional Services",
      manufacturing: "Manufacturing & Industrial",
      other: "Other",
    };
    return labelMap[industry] || industry;
  };

  const getConnectionTypeIcon = (type: string) => {
    return type === "mcp" ? (
      <Server className="h-4 w-4" />
    ) : (
      <Wifi className="h-4 w-4" />
    );
  };

  const getConnectionTypeLabel = (type: string) => {
    return type === "mcp" ? "Model Context Protocol (MCP)" : "REST API";
  };

  // Extract agent type, use case, and industry from tags or category
  const agentType =
    agent.agentType ||
    agent.category ||
    agent.tags?.find((tag) =>
      [
        "conversational",
        "sales",
        "content",
        "analytics",
        "safety",
        "development",
      ].includes(tag),
    ) ||
    "conversational";

  const industry =
    agent.industry ||
    agent.tags?.find((tag) =>
      [
        "ecommerce",
        "healthcare",
        "finance",
        "education",
        "technology",
        "media",
        "consulting",
        "manufacturing",
        "other",
      ].includes(tag),
    ) ||
    "other";

  const useCase =
    agent.useCase ||
    agent.tags?.find(
      (tag) =>
        ![
          "conversational",
          "sales",
          "content",
          "analytics",
          "safety",
          "development",
          "ecommerce",
          "healthcare",
          "finance",
          "education",
          "technology",
          "media",
          "consulting",
          "manufacturing",
          "other",
        ].includes(tag),
    ) ||
    "General";

  const connectionType =
    agent.connectionType ||
    agent.tags?.find((tag) => ["mcp", "rest-api"].includes(tag)) ||
    "rest-api";

  return (
    <div className="space-y-4 lg:space-y-6">
      {/* Agent Information */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-gray-100">Agent Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
            <div>
              <label className="text-sm font-medium text-gray-300">
                Agent Name
              </label>
              <p className="text-sm text-gray-400 mt-1">
                {agent.name || "Not configured"}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-300">
                Description
              </label>
              <p className="text-sm text-gray-400 mt-1">
                {agent.description || "No description provided"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Agent Type & Use Case */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-gray-100">Agent Type & Use Case</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
            <div>
              <label className="text-sm font-medium text-gray-300">
                Agent Type
              </label>
              <div className="flex items-center space-x-2 mt-1">
                {getAgentTypeIcon(agentType)}
                <span className="text-sm text-gray-400">
                  {getAgentTypeLabel(agentType)}
                </span>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-300">
                Use Case
              </label>
              <p className="text-sm text-gray-400 mt-1">{useCase}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-300">
                Industry
              </label>
              <div className="flex items-center space-x-2 mt-1">
                <span className="text-sm">{getIndustryIcon(industry)}</span>
                <span className="text-sm text-gray-400">
                  {getIndustryLabel(industry)}
                </span>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-300">
                Version
              </label>
              <p className="text-sm text-gray-400 mt-1">
                {agent.version || "v1.0.0"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Connection Configuration */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-gray-100">
            Connection Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
            <div>
              <label className="text-sm font-medium text-gray-300">
                Connection Type
              </label>
              <div className="flex items-center space-x-2 mt-1">
                {getConnectionTypeIcon(connectionType)}
                <span className="text-sm text-gray-400">
                  {getConnectionTypeLabel(connectionType)}
                </span>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-300">
                API Key Status
              </label>
              <p className="text-sm text-gray-400 mt-1">
                {config.apiKey ? "Configured" : "Not configured"}
              </p>
            </div>
          </div>

          {connectionType === "mcp" ? (
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-300">
                  Server URL
                </label>
                <p className="text-sm text-gray-400 mt-1">
                  {config.serverUrl || "Not configured"}
                </p>
              </div>
              {config.httpHeaders && config.httpHeaders.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-gray-300">
                    HTTP Headers
                  </label>
                  <div className="mt-2 space-y-1">
                    {config.httpHeaders.map((header, index) => (
                      <div key={index} className="text-sm text-gray-400">
                        {header.key}: {header.value}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div>
              <label className="text-sm font-medium text-gray-300">
                Endpoint URL
              </label>
              <p className="text-sm text-gray-400 mt-1">
                {config.endpoint || "Not configured"}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Model Configuration */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-gray-100">Model Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
            <div>
              <label className="text-sm font-medium text-gray-300">
                Model Version
              </label>
              <p className="text-sm text-gray-400 mt-1">
                {config.modelVersion || agent.model || "Not configured"}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-300">
                Max Tokens
              </label>
              <p className="text-sm text-gray-400 mt-1">
                {config.maxTokens || "Not configured"}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-300">
                Temperature
              </label>
              <p className="text-sm text-gray-400 mt-1">
                {config.temperature || "Not configured"}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-300">
                Timeout
              </label>
              <p className="text-sm text-gray-400 mt-1">
                {config.timeout ? `${config.timeout}ms` : "Not configured"}
              </p>
            </div>
          </div>

          {config.features && config.features.length > 0 && (
            <div>
              <label className="text-sm font-medium text-gray-300">
                Features
              </label>
              <div className="flex flex-wrap gap-2 mt-2">
                {config.features.map((feature, index) => (
                  <Badge
                    key={index}
                    variant="secondary"
                    className="bg-gray-700 text-gray-300"
                  >
                    {feature}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Test Configuration */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-gray-100">Test Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
            <div>
              <label className="text-sm font-medium text-gray-300">
                Total Tests
              </label>
              <p className="text-sm text-gray-400 mt-1">
                {testResults.totalTests}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-300">
                Average Score
              </label>
              <p className="text-sm text-gray-400 mt-1">
                {testResults.averageScore}%
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-300">
                Passed Tests
              </label>
              <p className="text-sm text-gray-400 mt-1">
                {testResults.passedTests}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-300">
                Failed Tests
              </label>
              <p className="text-sm text-gray-400 mt-1">
                {testResults.failedTests}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
